import { Fragment, useEffect } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { useCart } from '@/hooks/useCart'
import { useAuth } from '@/hooks/useAuth'
import { resolveUnitPrice } from '@/lib/ecommerce/price'
import {
  evictSoldOrRedirect,
  readSoldEvictionToast,
  clearSoldEvictionToast,
  showSoldEvictionToast,
} from '@/lib/ecommerce/availability'
import { navigateTo } from '@/lib/navigate'
import FlipButton from '@/components/ui/FlipButton'
import type { CartPagePayload } from '@/types/ecommerce'

/*
  Shopping Cart page (MIG) — Figma node 270:1035 ("Shopping Cart").

  Layout (per Figma):
    Header             "Selected Artworks"  (alfarn-2 700, 100px)
    Main Container     padding: 80px 24px
      Cart Container   gap: 48px between sections
        12px black hr
        Cart item × N  (image 360×360, 80px gap, details flex-1, price/remove right)
        12px black hr  (between & after items)
        Frame 115      gap 48px from items
          Subtotal Container
            12px black hr
            "SUBTOTAL - $X"  (right, 40px Light + Bold)
            12px black hr
          Shipping Container
            "Shipping costs are to be determined after purchase via email quote"
            CHECK OUT button (72px tall, 48×16 padding, 40px Poppins Light)

  No discount-code form, no Qty — the design treats every artwork as a
  single original. Lease items still go through the Lease Enquiry Form,
  so cart items always render "Purchase Price".

  A "Continue shopping" back link sits leftmost in the action row
  alongside the Check out button (Teamwork 26087637 follow-up).

  Styling lives in src/styles/pages/cart.css.
*/

const REMOVE_ICON = (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden="true"
    className="mig-cart-item-remove-icon"
  >
    <path d="M3 3 L29 29 M29 3 L3 29" stroke="#000000" strokeWidth="2" />
  </svg>
)

const HR_12 = <div aria-hidden="true" className="mig-cart-hr-12" />

function formatPrice(amount: number): string {
  return `$${amount.toLocaleString('en-AU', {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`
}

/* Right-aligned label+value row used inside the cart's totals block. */
function SummaryRow({
  label,
  value,
  bold,
}: {
  label: string
  value: string
  bold?: boolean
}) {
  return (
    <p className="mig-cart-summary-row" data-bold={!!bold}>
      <span className="mig-cart-summary-row-label">{label}</span>
      <span className="mig-cart-summary-row-value">{value}</span>
    </p>
  )
}

export default function Cart() {
  const payload = usePageData<CartPagePayload>() ?? {
    is_authenticated: false,
    user_uuid: null,
    session_id: null,
    contact_email: null,
  }
  const { items, subtotal, loading, error, refresh, removeItem } = useCart()
  const { contactUuid } = useAuth()
  const totals = payload.order_summary
  // payload.order_summary is computed server-side at page render via
  // compute_order_summary.liquid; it doesn't auto-update when the
  // buyer removes a cart item client-side. To keep Subtotal / Tax /
  // Total responsive to removeItem we scale the server-supplied
  // summary by the ratio of the current React-side subtotal
  // (recomputed from items on every refresh) to the server-supplied
  // subtotal. Tax (GST, fixed % per item) scales linearly with
  // subtotal, so the scaled values stay accurate as items disappear.
  // When subtotal hits 0 (last item removed) the factor is 0 and
  // every line reads 0.00.
  const scaleFactor =
    totals && totals.sub_total_amount > 0
      ? subtotal / totals.sub_total_amount
      : 1
  const display = totals
    ? {
        subtotal,
        tax: totals.tax_amount * scaleFactor,
        total: totals.total_amount * scaleFactor,
      }
    : { subtotal, tax: 0, total: subtotal }

  useEffect(() => {
    refresh()
  }, [refresh])

  // MIG one-of-a-kind guard. On every cart load we ask the
  // marketplace whether anything in this cart has been Sold by
  // another buyer mid-flight.
  useEffect(() => {
    const pending = readSoldEvictionToast()
    if (pending) {
      showSoldEvictionToast(pending.names, pending.reasons)
      clearSoldEvictionToast()
    }
  }, [])

  useEffect(() => {
    if (!items.length) return
    let cancelled = false
    evictSoldOrRedirect({ items, contactUuid, stay: true })
      .then((res) => {
        if (cancelled || res.removed === 0) return
        showSoldEvictionToast(res.names, res.reasons)
        refresh()
      })
      .catch(() => { /* submit-time gate covers us */ })
    return () => { cancelled = true }
  }, [items, contactUuid, refresh])

  const hasItems = items.length > 0

  return (
    <section className="mig-cart-section">
      <h1 className="mig-cart-title">Selected Artworks</h1>

      {error && (
        <p role="alert" className="mig-cart-error">{error}</p>
      )}

      {!hasItems && !loading && (
        <p className="mig-cart-empty">
          Your cart is empty.{' '}
          <a href="/products">Browse the Marketplace</a>
          .
        </p>
      )}

      {hasItems && (
        <div className="mig-cart-list">
          {/* Top rule */}
          {HR_12}

          {items.map((item, idx) => {
            const product = item.product
            const unit = resolveUnitPrice(product)
            const line = unit * (item.quantity ?? 0)
            const galleryFallback = product?.gallery_images?.find((g) => g?.file?.url)?.file?.url
            const imageUrl =
              product?.main_image?.url ??
              product?.product_image?.url ??
              galleryFallback ??
              null
            const artistName = product?.artist_name?.trim() || ''
            const productName = product?.product_name ?? 'Artwork'

            return (
              <Fragment key={item.uuid ?? idx}>
                <div className="mig-cart-item">
                  <div className="mig-cart-item-row">
                    {/* Image — 360×360, gray fallback with "Artwork" label */}
                    <div
                      className="mig-cart-item-image"
                      style={imageUrl ? ({ '--bg-image': `url("${imageUrl}")` } as React.CSSProperties) : undefined}
                      aria-hidden="true"
                    >
                      {!imageUrl && (
                        <span className="mig-cart-item-image-placeholder">Artwork</span>
                      )}
                    </div>

                    {/* Details — flex 1 */}
                    <div className="mig-cart-item-details">
                      {/* Description column */}
                      <div className="mig-cart-item-info">
                        <p className="mig-cart-item-artist">{artistName || productName}</p>
                        {artistName && (
                          <p className="mig-cart-item-product">{productName}</p>
                        )}
                      </div>

                      {/* Price + Remove column */}
                      <div className="mig-cart-item-meta">
                        <div className="mig-cart-item-price">
                          <span>Purchase Price</span>
                          <span>{formatPrice(line)}</span>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeItem(item.uuid)}
                          disabled={loading}
                          aria-label={`Remove ${productName}`}
                          className="mig-cart-item-remove"
                          data-pending={loading}
                        >
                          {REMOVE_ICON}
                          <span className="mig-cart-item-remove-label">Remove</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Divider between items — last item omits it because the
                    summary section that follows renders its own leading rule. */}
                {idx < items.length - 1 && HR_12}
              </Fragment>
            )
          })}

          {/* Subtotal section. The 12px black rule sits OUTSIDE the
              two-column grid so it stretches edge-to-edge of the cart
              list, matching the original implementation. Below it, the
              grid right-clusters: labels right-align in column 1, amounts
              right-align in column 2, so SUBTOTAL / TAX / PROCESSING FEE
              all anchor to the same right edges regardless of label length. */}
          <div className="mig-cart-summary">
            {HR_12}
            {/* Totals + shipping disclaimer wrapped together so the
                outer .mig-cart-summary flex gap (var(--space-2xl))
                doesn't drop 48px between them. Internal column-flex
                gives them their own (tight) gap. */}
            <div className="mig-cart-summary-totals">
              <div className="mig-cart-summary-rows">
                <SummaryRow label="Subtotal" value={formatPrice(display.subtotal)} />
                <SummaryRow label="Tax" value={formatPrice(display.tax)} />
                <SummaryRow label="Total" value={formatPrice(display.total)} bold />
              </div>

              <p className="mig-cart-shipping-disclaimer">
                Shipping costs are to be determined after purchase via email quote
              </p>
            </div>

            {/* Action row — Continue shopping (left) + Check out (right). */}
            <div className="mig-cart-action-row">
              <a
                href="/marketplace"
                className="mig-cart-continue-shopping"
                onClick={(event) => {
                  const ref = typeof document !== 'undefined' ? document.referrer : ''
                  let sameOriginReferrer = false
                  try {
                    sameOriginReferrer = !!ref && new URL(ref).origin === window.location.origin
                  } catch {
                    sameOriginReferrer = false
                  }
                  if (sameOriginReferrer) {
                    event.preventDefault()
                    window.history.back()
                  }
                }}
              >
                Continue shopping
              </a>
              <FlipButton
                onClick={navigateTo('/checkout')}
                className="mig-cart-checkout"
              >
                Check out
              </FlipButton>
            </div>
          </div>
        </div>
      )}

      {/* Surface auth flag for analytics / future gating without rendering */}
      <span data-auth={String(payload.is_authenticated)} className="mig-cart-auth-flag" />
    </section>
  )
}
